// src/components/AddHeatDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    IconButton,
    DialogContentText,
    Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { createHeat, updateHeat } from '../api/competitionApi';

export default function AddHeatDialog({ open, onClose, schedule, heat }) {
    const [formData, setFormData] = useState({
        name: '',
        numberOfLanes: 6
    });
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (heat) {
            setFormData({
                name: heat.name || '',
                numberOfLanes: heat.lanes?.length || 6
            });
        } else {
            setFormData({ name: '', numberOfLanes: 6 });
        }
    }, [heat, open]);

    const handleSubmit = async () => {
        if (!formData.name || !formData.numberOfLanes) {
            setAlertMessage('Please fill in all required fields');
            setAlertOpen(true);
            return;
        }

        if (formData.numberOfLanes < 1 || formData.numberOfLanes > 20) {
            setAlertMessage('Number of lanes must be between 1 and 20');
            setAlertOpen(true);
            return;
        }

        try {
            setLoading(true);
            setErrorMessage('');
            if (heat) {
                await updateHeat(heat.id, {
                    name: formData.name,
                    numberOfLanes: formData.numberOfLanes
                });
            } else {
                await createHeat(schedule.id, {
                    name: formData.name,
                    numberOfLanes: formData.numberOfLanes
                });
            }
            onClose();
        } catch (error) {
            console.error('Error saving heat:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to save heat');
        } finally {
            setLoading(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        setAlertMessage('');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {heat ? 'Edit Heat' : 'Add Heat'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Heat Name *"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Heat 1, Heat 2"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Number of Lanes *"
                            value={formData.numberOfLanes}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                numberOfLanes: parseInt(e.target.value) || 0
                            }))}
                            inputProps={{ min: 1, max: 20 }}
                            helperText="Enter the number of lanes for this heat (1-20)"
                        />
                    </Grid>

                    {schedule && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                This heat will be added to: <strong>{schedule.name}</strong>
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>

            {/* Alert Dialog */}
            <Dialog
                open={alertOpen}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Warning
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleAlertClose}
                        color="primary"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}